import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { Plus, FileUp } from '@carboledger/ui/icons';
import { ROUTES } from '@/app/routes';
import { PageHeader } from '@/components/layout';
import { useTranslations, useAssistantFilters } from '@/hooks';
import { AddAttachmentModal, DiscrepancyModal, useLinkableCandidates } from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { ConfirmDialog } from '@/components/feedback';
import { buildShipmentLinksPayload } from '@/lib/mappers/links';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useDeleteIncomingShipmentMutation } from '../api/incomingShipmentsApi';
import { linksApi, useLinkContractShipmentsMutation } from '@/lib/api/linksApi';
import {
  LinkContractsModal,
  LinkContractsForShipment,
  LINK_CONTRACT_FLOWS,
  buildShipmentLinkDetailFields,
  aggregateLinkRows,
} from '@/components/common';
import { buildShipmentMenuActions } from '../constants/incomingShipmentActions';
import { buildShipmentRowDiscrepancySections } from '../constants/incomingShipmentDiscrepancy';
import { EMPTY_LIST } from '@/lib/utils';
import styles from './IncomingShipmentsPage.module.scss';
import { IncomingShipmentsTable } from '../components/IncomingShipmentsTable';

// Inbounds ▸ Incoming Shipments list, fed by GET /:module/batch-group.

// Identity rows the bulk card only shows when every selected shipment agrees on them.
const SHIPMENT_IDENTITY_KEYS = [
  'materialName',
  'supplierName',
  'facilityName',
  'terminal',
  'dateOfDispatch',
  'dateOfDischarge',
  'totalContractsLinked',
  'totalDeclarationsReceived',
];

export const IncomingShipmentsPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module } = useParams();
  const assistantFilters = useAssistantFilters();

  const [discrepancy, setDiscrepancy] = useState(null);
  const discrepancyRow = discrepancy?.row ?? null;
  const [deleteRow, setDeleteRow] = useState(null);
  const [attachmentRow, setAttachmentRow] = useState(null);
  const [linkRow, setLinkRow] = useState(null);
  const [linkShipments, setLinkShipments] = useState(EMPTY_LIST);
  const [linkSearch, setLinkSearch] = useState('');
  const tableRef = useRef(null);
  const [linkContractShipments, { isLoading: isSavingLinks }] = useLinkContractShipmentsMutation();

  const handleOpenDiscrepancy = useCallback(
    (row, unitType) => setDiscrepancy({ row, unitType }),
    [],
  );
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancy(null), []);

  const handleAdd = useCallback(() => {
    navigate(ROUTES.incomingShipmentCreate(module));
  }, [navigate, module]);
  const handleViewShipment = useCallback(
    (row) => {
      navigate(ROUTES.incomingShipmentDetail(module, row.id));
    },
    [navigate, module],
  );
  const handleAddDeclarations = useCallback(
    (row) => {
      navigate(ROUTES.incomingShipmentAddDeclarations(module, row.id));
    },
    [navigate, module],
  );
  const handleLinkContracts = useCallback((row) => {
    setLinkShipments(EMPTY_LIST);
    setLinkRow(row);
  }, []);

  // <Table> hands the bulk action its own selection, so the page keeps no copy.
  const handleBulkLinkContracts = useCallback((rows) => {
    setLinkRow(null);
    setLinkShipments(rows);
  }, []);

  const handleCloseLink = useCallback(() => {
    setLinkRow(null);
    setLinkShipments(EMPTY_LIST);
    setLinkSearch('');
  }, []);

  const handleLinkSearchChange = useCallback((term) => setLinkSearch(term), []);
  const handleAddAttachment = useCallback((row) => setAttachmentRow(row), []);
  const handleCloseAttachment = useCallback(() => setAttachmentRow(null), []);
  const handleEdit = useCallback(
    (row) => {
      navigate(ROUTES.incomingShipmentEdit(module, row.id));
    },
    [navigate, module],
  );
  const [deleteIncomingShipment] = useDeleteIncomingShipmentMutation();

  // Ids with a DELETE in flight — gates the double-click RTK Query won't dedupe.
  const deletingIdsRef = useRef(new Set());

  const handleDelete = useCallback((row) => setDeleteRow(row), []);
  const handleCancelDelete = useCallback(() => setDeleteRow(null), []);

  const handleConfirmDelete = useCallback(async () => {
    const id = deleteRow?.id;
    if (id == null || deletingIdsRef.current.has(id)) return;
    deletingIdsRef.current.add(id);
    try {
      await deleteIncomingShipment({ module, id }).unwrap();
      setDeleteRow(null);
    } catch {
      // notifyApiErrors already surfaced it.
    } finally {
      deletingIdsRef.current.delete(id);
    }
  }, [deleteIncomingShipment, module, deleteRow]);

  const importIcon = useMemo(() => <FileUp />, []);
  const addIcon = useMemo(() => <Plus />, []);

  const headerActions = useMemo(
    () => (
      <>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          // TODO: enable once the shared .xlsx import wizard exists.
          disabled
        >
          {t('INCOMING_SHIPMENTS_IMPORT')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          onClick={handleAdd}
        >
          {t('INCOMING_SHIPMENTS_ADD')}
        </Button>
      </>
    ),
    [t, importIcon, addIcon, handleAdd],
  );

  const isBulkLink = linkShipments.length > 0;

  const bulkQueryArgs = useMemo(
    () => linkShipments.map((row) => ({ shipmentId: row.id, search: linkSearch })),
    [linkShipments, linkSearch],
  );

  const { candidates: bulkCandidates, loading: isLoadingBulkCandidates } = useLinkableCandidates(
    linksApi.endpoints.getLinkableContracts,
    bulkQueryArgs,
  );

  const bulkTitle = t('LINK_CONTRACTS_TITLE', {
    shipmentId: linkShipments.map((row) => row.shipmentId).join(', '),
  });

  // The selection reads as one shipment, so the bulk card is the row card's own builder.
  const bulkShipment = useMemo(
    () => aggregateLinkRows(linkShipments, SHIPMENT_IDENTITY_KEYS),
    [linkShipments],
  );

  const bulkDetailFields = useCallback(
    (unitType, assignedInUnit) =>
      buildShipmentLinkDetailFields(t, bulkShipment, unitType, assignedInUnit),
    [t, bulkShipment],
  );

  const rowDetailFields = useCallback(
    (unitType) => (linkRow ? buildShipmentLinkDetailFields(t, linkRow, unitType) : EMPTY_LIST),
    [linkRow, t],
  );

  const handleSaveBulkLinks = useCallback(
    async (entries, pickedContractId) => {
      const result = await linkContractShipments({
        module,
        contractId: pickedContractId,
        shipmentIds: linkShipments.map((row) => row.id),
        links: buildShipmentLinksPayload(entries),
      });
      if (result.error) return;
      tableRef.current?.clearSelection();
      handleCloseLink();
    },
    [linkContractShipments, module, linkShipments, handleCloseLink],
  );

  const bulkActions = useMemo(
    () => [
      {
        id: 'link-contracts',
        label: t('INCOMING_SHIPMENTS_LINK_CONTRACTS'),
        onClick: handleBulkLinkContracts,
      },
    ],
    [t, handleBulkLinkContracts],
  );

  // No View action — the Shipment ID cell is the row's link to its detail page.
  const rowActions = useMemo(
    () => [
      {
        id: 'add-declarations',
        label: t('ACTION_ADD_DECLARATIONS'),
        variant: ButtonVariants.tertiary,
        onClick: handleAddDeclarations,
      },
      {
        id: 'link-contracts',
        label: t('INCOMING_SHIPMENTS_LINK_CONTRACTS'),
        variant: ButtonVariants.tertiary,
        onClick: handleLinkContracts,
      },
    ],
    [t, handleAddDeclarations, handleLinkContracts],
  );

  const rowMenuActions = useMemo(
    () =>
      buildShipmentMenuActions(t, {
        onAddAttachment: handleAddAttachment,
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [t, handleAddAttachment, handleEdit, handleDelete],
  );

  const discrepancies = discrepancyRow?.discrepancyItems ?? [];

  const discrepancySections = useMemo(
    () => buildShipmentRowDiscrepancySections(t, discrepancyRow, discrepancy?.unitType),
    [discrepancyRow, discrepancy, t],
  );

  // Shown when the account holds no shipments at all, not when a filter matches none.
  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('INCOMING_SHIPMENTS_EMPTY_TITLE'),
      description: t('INCOMING_SHIPMENTS_EMPTY_DESCRIPTION'),
      primaryLabel: t('INCOMING_SHIPMENTS_ADD'),
      onPrimaryClick: handleAdd,
      primaryIcon: addIcon,
    }),
    [t, handleAdd, addIcon],
  );

  const noResults = useMemo(
    () => ({
      title: t('INCOMING_SHIPMENTS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <div className={styles.content}>
      <PageHeader title={t('INCOMING_SHIPMENTS_TITLE')} actions={headerActions} />
      <IncomingShipmentsTable
        tableRef={tableRef}
        extraFilters={assistantFilters}
        emptyState={emptyState}
        noResults={noResults}
        onOpenShipment={handleViewShipment}
        onOpenDiscrepancy={handleOpenDiscrepancy}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        bulkActions={bulkActions}
      />

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.shipmentId}
        discrepancies={discrepancies}
        sections={discrepancySections}
      />

      <LinkContractsForShipment
        module={module}
        shipmentDbId={linkRow?.id}
        shipmentLabel={linkRow?.shipmentId}
        detailFields={rowDetailFields}
        unassignedByUnit={linkRow?.unassignedByUnit}
        open={linkRow != null}
        onClose={handleCloseLink}
      />

      {/* Closing unmounts it, so nothing it held survives into the next open. */}
      {isBulkLink ? (
        <LinkContractsModal
          open
          onClose={handleCloseLink}
          title={bulkTitle}
          detailFields={bulkDetailFields}
          contracts={bulkCandidates}
          loading={isLoadingBulkCandidates}
          flow={LINK_CONTRACT_FLOWS.manyShipments}
          shipments={linkShipments}
          isSaving={isSavingLinks}
          onSearchChange={handleLinkSearchChange}
          onSave={handleSaveBulkLinks}
        />
      ) : null}

      <AddAttachmentModal
        open={attachmentRow != null}
        onClose={handleCloseAttachment}
        title={t('INCOMING_SHIPMENTS_ADD_ATTACHMENT_MODAL_TITLE', {
          shipmentId: attachmentRow?.shipmentId,
        })}
        target={ATTACHMENT_TARGETS.shipment}
        module={module}
        entityId={attachmentRow?.id}
      />

      <ConfirmDialog
        open={deleteRow != null}
        title={t('INCOMING_SHIPMENTS_DELETE_TITLE')}
        bodyTitle={t('INCOMING_SHIPMENTS_DELETE_BODY_TITLE', { shipmentId: deleteRow?.shipmentId })}
        description={t('INCOMING_SHIPMENTS_DELETE_DESCRIPTION', {
          materialName: deleteRow?.materialName,
          supplierName: deleteRow?.supplierName,
        })}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
